// components/Delivery/AuditLogModal.tsx
import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
} from "reactstrap";

interface AuditLog {
  id: number;
  action: string;
  action_display: string;
  description: string;
  old_status: string | null;
  new_status: string | null;
  old_quantity: string | null;
  new_quantity: string | null;
  performed_by: number;
  performed_by_name: string;
  content_type_name: string;
  object_id: number;
  created_at: string;
}

interface AuditLogModalProps {
  isOpen: boolean;
  toggle: () => void;
  auditLogs: AuditLog[];
  title: string;
}

const AuditLogModal: React.FC<AuditLogModalProps> = ({
  isOpen,
  toggle,
  auditLogs,
  title
}) => {
  // Function to get action badge color
  const getActionBadgeColor = (action: string) => {
    const actionColors: { [key: string]: string } = {
      'status_change': 'primary',
      'delivery_received': 'success',
      'defect_reported': 'danger',
      're_delivery_initiated': 'info',
      'split_created': 'warning',
      'split_updated': 'secondary',
      'quantity_updated': 'dark',
    };
    return actionColors[action] || 'secondary';
  };

  // Function to format date for display
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-GB'),
      time: date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      fullDate: date.toLocaleString('en-GB')
    };
  };

  // Function to get action icon
  const getActionIcon = (action: string) => {
    const actionIcons: { [key: string]: string } = {
      'status_change': 'ri-refresh-line',
      'delivery_received': 'ri-check-double-line',
      'defect_reported': 'ri-error-warning-line',
      're_delivery_initiated': 'ri-refresh-line',
      'split_created': 'ri-dashboard-horizontal-line',
      'split_updated': 'ri-edit-line',
      'quantity_updated': 'ri-number-1',
    };
    return actionIcons[action] || 'ri-history-line';
  };

  // Function to render quantity changes
  const renderQuantityChange = (log: AuditLog) => {
    if (log.old_quantity && log.new_quantity) {
      return (
        <div className="mt-1">
          <small className="text-muted">
            Quantity: {log.old_quantity} → <strong>{log.new_quantity}</strong>
          </small>
        </div>
      );
    }
    return null;
  };

  // Function to render status changes
  const renderStatusChange = (log: AuditLog) => {
    if (log.old_status && log.new_status) {
      return (
        <div className="mt-1">
          <small className="text-muted">
            Status: <span className="text-danger">{log.old_status}</span> →{' '}
            <span className="text-success">{log.new_status}</span>
          </small>
        </div>
      );
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <i className="ri-history-line me-2"></i>
        Audit Trail - {title}
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {auditLogs.length === 0 ? (
          <div className="text-center py-4">
            <i className="ri-inbox-line display-4 text-muted"></i>
            <h5 className="mt-3 text-muted">No Audit Logs</h5>
            <p className="text-muted">No activity recorded yet.</p>
          </div>
        ) : (
          <div className="audit-timeline">
            {auditLogs.map((log, index) => {
              const dateTime = formatDateTime(log.created_at);
              return (
                <div key={log.id} className="audit-log-item mb-4">
                  {/* Timeline connector */}
                  {index < auditLogs.length - 1 && (
                    <div className="timeline-connector"></div>
                  )}
                  
                  <div className="d-flex">
                    {/* Icon */}
                    <div className="flex-shrink-0 me-3">
                      <div className={`avatar-xs bg-soft-${getActionBadgeColor(log.action)} text-${getActionBadgeColor(log.action)} rounded-circle d-flex align-items-center justify-content-center`}>
                        <i className={`${getActionIcon(log.action)} fs-14`}></i>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div>
                          <Badge color={getActionBadgeColor(log.action)} className="fs-11">
                            {log.action_display}
                          </Badge>
                        </div>
                        <div className="text-end">
                          <small className="text-muted d-block">{dateTime.date}</small>
                          <small className="text-muted">{dateTime.time}</small>
                        </div>
                      </div>
                      
                      <p className="mb-1 fs-13">{log.description}</p>
                      
                      {/* Status Change */}
                      {renderStatusChange(log)}
                      
                      {/* Quantity Change */}
                      {renderQuantityChange(log)}
                      
                      {/* Performed By */}
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="ri-user-line align-middle me-1"></i>
                          By: <strong>{log.performed_by_name}</strong>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="text-muted fs-12">
          <i className="ri-information-line me-1"></i>
          Showing {auditLogs.length} audit log{auditLogs.length !== 1 ? 's' : ''}
        </div>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>

      <style>{`
        .audit-timeline {
          position: relative;
          padding-left: 0;
        }
        
        .audit-log-item {
          position: relative;
          padding-left: 0;
        }
        
        .timeline-connector {
          position: absolute;
          left: 11px;
          top: 28px;
          bottom: -28px;
          width: 2px;
          background-color: #e9ecef;
          z-index: 1;
        }
        
        .audit-log-item:last-child .timeline-connector {
          display: none;
        }
        
        .avatar-xs {
          width: 24px;
          height: 24px;
          z-index: 2;
          position: relative;
        }
      `}</style>
    </Modal>
  );
};

export default AuditLogModal;